import { useEffect, useState } from 'react';
import { useControllerFlowStepsContext } from '../../../hooks/ControllerFlowStepsContext';
import Button from '../../../components/Button/Button';
import ButtonLabel from '../../../components/Button/ButtonLabel';
import FormFieldToggle from '../../../components/FormField/FormFieldToggle';
import Icon from '../../../components/Icon/Icon';
import Loading from '../../../components/Loading/Loading';
import { LINK_CENTRAL_HELP } from '../../../data/Constants';
import { useAuthContext } from '../../../hooks/AuthContext';
import {
  getTypesCommunicate,
  getPreferencesAnnouncements,
  updateTypesCommunicate,
  updatePreferencesAnnouncements
} from '../../../services/user';

const SUBLINKS = [
  { title: 'Tipos de comunicados', id: 'tipos-de-comunicados' },
  { title: 'Assuntos dos comunicados', id: 'assuntos-comunicados' },
  { title: 'Notificações', id: 'notificacoes' }
];

interface ITypesCommunicate {
  email: boolean;
  sms: boolean;
  whatsapp: boolean;
}

interface ISubject {
  title: string;
  value: string;
  active: boolean;
}

const DEFAULT_SUBJECTS: ISubject[] = [
  { title: 'Programação da TV', value: 'tvSchedule', active: false },
  { title: 'Conteúdo online', value: 'onlineContent', active: false },
  { title: '+SBT', value: 'maisSbt', active: false },
  { title: 'SBT News', value: 'sbtNews', active: false },
  { title: 'SBT Games', value: 'sbtGames', active: false },
  { title: 'SBT Sports', value: 'sbtSports', active: false },
  { title: 'Produtos SBT', value: 'sbtProducts', active: false },
  { title: 'Promoções', value: 'promotions', active: false },
  { title: 'Parceiros do SBT', value: 'sbtPartners', active: false },
  { title: 'Institucional', value: 'institutional', active: false },
  { title: 'Pesquisas', value: 'researches', active: false }
];

function TiposComunicadosPanel() {
  const { checkAuth } = useAuthContext();
  const { showErrorMessageServer, setAlertBox } =
    useControllerFlowStepsContext();
  const [types, setTypes] = useState<ITypesCommunicate>({
    email: false,
    sms: false,
    whatsapp: false
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadTypes();
  }, []);

  async function loadTypes() {
    setLoadingData(true);
    try {
      const response = await getTypesCommunicate();
      setTypes({
        email: response.optEmail,
        sms: response.optSms,
        whatsapp: response.optWhatsapp
      });
    } catch {
      showErrorMessageServer();
    } finally {
      setLoadingData(false);
    }
  }

  async function saveTypes() {
    setLoading(true);
    try {
      await updateTypesCommunicate(checkAuth()?.email, types);
      setAlertBox({ message: 'Preferências atualizadas', error: false });
    } catch {
      showErrorMessageServer();
    } finally {
      setLoading(false);
    }
  }

  function toggle(key: keyof ITypesCommunicate) {
    setTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  const items: { label: string; key: keyof ITypesCommunicate }[] = [
    { label: 'E-mails', key: 'email' },
    { label: 'SMS', key: 'sms' },
    { label: 'WhatsApp', key: 'whatsapp' }
  ];

  if (loadingData) {
    return (
      <div className="up-prefs-panel up-prefs-panel--loading">
        <Loading />
      </div>
    );
  }

  return (
    <div className="up-prefs-panel">
      <p className="up-prefs-panel__description">
        Controle quais tipos de comunicados você quer receber e tenha acesso a
        todas as novidades do SBT.
      </p>
      <p className="up-prefs-panel__note">
        Suas alterações demoram até 72h para entrar em vigor.
      </p>
      <div className="up-prefs-panel__list">
        {items.map(({ label, key }) => (
          <div key={key} className="up-prefs-panel__item">
            <span className="up-prefs-panel__item-label">{label}</span>
            <div className="up-prefs-panel__item-toggle">
              <span className="up-prefs-panel__item-status">
                {types[key] ? 'ativado' : 'desativado'}
              </span>
              <FormFieldToggle
                value={key}
                checked={types[key]}
                callbackChange={() => toggle(key)}
              />
            </div>
          </div>
        ))}
      </div>
      <Button
        className="mt-24 mbi-0"
        onClick={saveTypes}
        loading={loading}
        disabled={loading}
      >
        <ButtonLabel loading={loading}>salvar</ButtonLabel>
      </Button>
    </div>
  );
}

function AssuntosComunicadosPanel() {
  const { showErrorMessageServer, setAlertBox } =
    useControllerFlowStepsContext();
  const [subjects, setSubjects] = useState<ISubject[]>(DEFAULT_SUBJECTS);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadSubjects();
  }, []);

  async function loadSubjects() {
    setLoadingData(true);
    try {
      const response = await getPreferencesAnnouncements();
      setSubjects(
        DEFAULT_SUBJECTS.map((s) => ({
          ...s,
          active: (response as any)[s.value] ?? s.active
        }))
      );
    } catch {
      showErrorMessageServer();
    } finally {
      setLoadingData(false);
    }
  }

  async function saveSubjects() {
    setLoading(true);
    try {
      const payload: any = {};
      subjects.forEach((s) => {
        payload[s.value] = s.active;
      });
      await updatePreferencesAnnouncements(payload);
      setAlertBox({ message: 'Preferências atualizadas', error: false });
    } catch {
      showErrorMessageServer();
    } finally {
      setLoading(false);
    }
  }

  function toggleSubject(index: number) {
    setSubjects((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], active: !copy[index].active };
      return copy;
    });
  }

  if (loadingData) {
    return (
      <div className="up-prefs-panel up-prefs-panel--loading">
        <Loading />
      </div>
    );
  }

  return (
    <div className="up-prefs-panel">
      <p className="up-prefs-panel__description">
        Escolha os assuntos dos comunicados que você recebe.
      </p>
      <div className="up-prefs-panel__help">
        <Icon name="interrogation" className="up-prefs-panel__help-icon" />
        <span>
          Tem dúvidas?{' '}
          <span
            className="link"
            onClick={() => window.open(LINK_CENTRAL_HELP, '_blank')}
          >
            Clique aqui
          </span>{' '}
          para ver os detalhes.
        </span>
      </div>
      <div className="up-prefs-panel__list">
        {subjects.map((s, idx) => (
          <div key={s.value} className="up-prefs-panel__item">
            <span className="up-prefs-panel__item-label">{s.title}</span>
            <div className="up-prefs-panel__item-toggle">
              <span className="up-prefs-panel__item-status">
                {s.active ? 'ativado' : 'desativado'}
              </span>
              <FormFieldToggle
                value={s.value}
                checked={s.active}
                callbackChange={() => toggleSubject(idx)}
              />
            </div>
          </div>
        ))}
      </div>
      <Button
        className="mt-24 mbi-0"
        onClick={saveSubjects}
        loading={loading}
        disabled={loading}
      >
        <ButtonLabel loading={loading}>salvar</ButtonLabel>
      </Button>
    </div>
  );
}

function NotificacoesPanel() {
  return (
    <div className="up-prefs-panel">
      <p className="up-prefs-panel__description">
        O controle de notificações dos nossos aplicativos é feito no seu
        celular. Para saber como gerenciar essas opções, acesse as instruções
        das empresas responsáveis pelo sistema operacional do seu celular:{' '}
        <a
          className="link strong"
          target="_blank"
          rel="noreferrer"
          href="https://support.apple.com/pt-br/HT201925"
        >
          iOS
        </a>{' '}
        ou{' '}
        <a
          className="link strong"
          target="_blank"
          rel="noreferrer"
          href="https://support.google.com/android/answer/9079661?hl=pt-BR"
        >
          Android
        </a>
        .
      </p>
    </div>
  );
}

const PANELS: Record<string, React.FC> = {
  'tipos-de-comunicados': TiposComunicadosPanel,
  'assuntos-comunicados': AssuntosComunicadosPanel,
  notificacoes: NotificacoesPanel
};

export default function PreferenciasTab() {
  const [selected, setSelected] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const currentSubLink = SUBLINKS[selected];
  const Panel = PANELS[currentSubLink.id];

  function toggleMenu() {
    setIsMenuOpen((prev) => !prev);
  }

  function handleNavSelect(index: number) {
    setSelected(index);
    setIsMenuOpen(false);
  }

  return (
    <div className="user-profile__content">
      <div className="user-profile__left-rail">
        <div
          className={`user-profile__left-rail-header${isMenuOpen ? ' user-profile__left-rail-header--open' : ''}`}
          onClick={toggleMenu}
        >
          <span>Preferências</span>
        </div>
        <div className="user-profile__left-rail-divider" />
        <div
          className={`user-profile__left-rail-nav${isMenuOpen ? ' user-profile__left-rail-nav--open' : ''}`}
        >
          {SUBLINKS.map((link, index) => (
            <button
              key={link.id}
              className={`user-profile__nav-item${selected === index ? ' user-profile__nav-item--active' : ''}`}
              onClick={() => handleNavSelect(index)}
            >
              {link.title}
            </button>
          ))}
        </div>
      </div>

      <div className="user-profile__main">
        <div className="user-profile__main-header">
          <span className="user-profile__main-title">
            {currentSubLink.title}
          </span>
        </div>
        <div className="user-profile__main-divider" />
        <Panel />
      </div>
    </div>
  );
}
